import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@voilajsx/uikit/page';
import { Button } from '@voilajsx/uikit/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@voilajsx/uikit/dialog';
import { Loader2, AlertTriangle, CheckCircle, ArrowRight, Wallet, Zap } from 'lucide-react';
import { Header, SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { config } from '../../auth/config';

const GOLD = '#FFD93D';

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price: number;
  isActive: boolean;
}

const BuyCreditsPage: React.FC = () => {
  const { isAuthenticated, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Custom amount
  const [customAmount, setCustomAmount] = useState('');

  // Dialog state
  const [selectedPack, setSelectedPack] = useState<CreditPack | null>(null);
  const [customPayAmount, setCustomPayAmount] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState<{ success: boolean; message: string; paymentId?: string } | null>(null);

  // Payment result from PayU redirect
  const [paymentStatus, setPaymentStatus] = useState<{ type: 'success' | 'failed' | 'error'; credits?: number } | null>(null);

  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      setPaymentStatus({ type: 'success', credits: parseInt(searchParams.get('credits') || '0', 10) });
      setSearchParams({}, { replace: true });
    } else if (payment === 'failed') {
      setPaymentStatus({ type: 'failed' });
      setSearchParams({}, { replace: true });
    } else if (payment === 'error') {
      setPaymentStatus({ type: 'error' });
      setSearchParams({}, { replace: true });
    }
  }, []);

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
    [config.auth.headers.auth]: `Bearer ${token}`,
  }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const packsRes = await fetch(`${config.api.baseUrl}/api/payment/packs`, {
        headers: { [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue }
      });
      if (!packsRes.ok) throw new Error(`HTTP ${packsRes.status}`);
      const packsJson = await packsRes.json();
      const raw = packsJson.data?.packs || packsJson.data || [];
      setPacks(raw.map((p: any) => ({
        ...p,
        price: p.price ?? p.priceInr ?? 0,
        isActive: p.isActive ?? (p.status === 'active'),
      })));

      if (isAuthenticated && token) {
        try {
          const balRes = await fetch(`${config.api.baseUrl}/api/credit/balance`, { headers: authHeaders() });
          if (balRes.ok) {
            const bj = await balRes.json();
            setBalance((bj.data || bj)?.balance ?? 0);
          }
        } catch { }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const bestValuePackId = React.useMemo(() => {
    const active = packs.filter(p => p.isActive);
    if (active.length === 0) return null;
    return active.reduce((best, p) => (p.price / p.credits < best.price / best.credits) ? p : best, active[0]).id;
  }, [packs]);

  const handleBuyPack = (pack: CreditPack) => {
    setSelectedPack(pack);
    setCustomPayAmount(null);
    setPurchaseResult(null);
    setDialogOpen(true);
  };

  const handleBuyCustom = () => {
    const amt = parseInt(customAmount, 10);
    if (!amt || amt < 1) return;
    setSelectedPack(null);
    setCustomPayAmount(amt);
    setPurchaseResult(null);
    setDialogOpen(true);
  };

  const handlePurchase = async () => {
    if (!token) return;
    const packId = selectedPack?.id;
    // For custom amount, we pass amount directly (no pack)
    if (!packId && !customPayAmount) return;

    setPurchasing(true);
    setPurchaseResult(null);

    try {
      const body: any = {};
      if (packId) {
        body.creditPackId = packId;
      } else {
        body.customAmount = customPayAmount;
      }

      const res = await fetch(`${config.api.baseUrl}/api/payment/create-order`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${res.status}`);
      }
      const data = (await res.json()).data;

      if (data.payuFormData && data.payuBaseUrl) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = data.payuBaseUrl;
        for (const [key, value] of Object.entries(data.payuFormData)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = String(value);
          form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
        return;
      }

      setPurchaseResult({ success: true, message: 'Order created.', paymentId: data?.paymentId });
    } catch (err: any) {
      setPurchaseResult({ success: false, message: err.message || 'Failed to create order' });
    } finally {
      setPurchasing(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedPack(null);
    setCustomPayAmount(null);
    setPurchaseResult(null);
  };

  if (!isAuthenticated) {
    return (
      <PageLayout size="full">
        <SEO title="Buy Credits" description="Purchase credits for plot designs" />
        <Header />
        <PageLayout.Content>
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
            <Wallet size={48} style={{ margin: '0 auto 16px', color: GOLD }} />
            <p style={{ fontSize: 18, marginBottom: 12 }}>Sign in to purchase credits</p>
            <Link to="/auth/login" style={{ color: GOLD, fontWeight: 600, textDecoration: 'underline' }}>Sign In</Link>
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  const dialogLabel = selectedPack
    ? `${selectedPack.name} — ₹${selectedPack.price}`
    : `Custom Top-Up — ₹${customPayAmount}`;
  const dialogCredits = selectedPack ? selectedPack.credits : customPayAmount;

  return (
    <PageLayout size="full">
      <SEO title="Top Up Wallet" description="Add credits to your wallet" />
      <Header />
      <PageLayout.Content>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px 80px' }}>

          {/* Payment result banner */}
          {paymentStatus && (
            <div style={{
              padding: '14px 20px', borderRadius: 10, marginBottom: 20,
              display: 'flex', alignItems: 'center', gap: 10,
              background: paymentStatus.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${paymentStatus.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
              color: paymentStatus.type === 'success' ? '#22c55e' : '#ef4444',
            }}>
              {paymentStatus.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                {paymentStatus.type === 'success'
                  ? `Payment successful! ₹${paymentStatus.credits || 0} added to your wallet.`
                  : paymentStatus.type === 'failed'
                    ? 'Payment cancelled or failed. No amount was charged.'
                    : 'Something went wrong. Please contact support.'}
              </span>
              <button onClick={() => setPaymentStatus(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}>✕</button>
            </div>
          )}

          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#fff', margin: '0 0 6px' }}>Top Up Wallet</h1>
            <p style={{ color: '#666', fontSize: 14, margin: 0 }}>Choose a pack or enter a custom amount</p>
          </div>

          {/* Balance card */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255,217,61,0.08), rgba(255,217,61,0.02))',
            border: '1px solid rgba(255,217,61,0.2)', borderRadius: 12, padding: '16px 20px',
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28,
          }}>
            <div style={{ width: 40, height: 40, background: 'rgba(255,217,61,0.12)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Wallet size={20} style={{ color: GOLD }} />
            </div>
            <div>
              <p style={{ fontSize: 12, color: '#888', margin: 0 }}>Current Balance</p>
              {loading
                ? <div style={{ width: 60, height: 24, background: '#222', borderRadius: 4 }} />
                : <p style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: 0 }}>₹{balance}</p>
              }
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', fontSize: 14, marginBottom: 20 }}>
              {error}
            </div>
          )}

          {/* Pack grid */}
          {!loading && !error && packs.filter(p => p.isActive).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 28 }}>
              {packs.filter(p => p.isActive).map(pack => {
                const isBest = pack.id === bestValuePackId;
                return (
                  <button
                    key={pack.id}
                    onClick={() => handleBuyPack(pack)}
                    style={{
                      position: 'relative', background: '#141414', border: isBest ? `2px solid ${GOLD}` : '1px solid #222',
                      borderRadius: 12, padding: '20px 16px', textAlign: 'center', cursor: 'pointer',
                      transition: 'border-color 0.2s, transform 0.15s',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = GOLD; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = isBest ? GOLD : '#222'; (e.currentTarget as HTMLElement).style.transform = 'none'; }}
                  >
                    {isBest && (
                      <span style={{
                        position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                        background: GOLD, color: '#111', fontSize: 10, fontWeight: 700, padding: '2px 10px',
                        borderRadius: 10, whiteSpace: 'nowrap',
                      }}>BEST VALUE</span>
                    )}
                    <p style={{ fontSize: 13, fontWeight: 600, color: GOLD, margin: '0 0 10px' }}>{pack.name}</p>
                    <p style={{ fontSize: 28, fontWeight: 800, color: '#fff', margin: '0 0 10px' }}>₹{pack.price}</p>
                    <div style={{ background: '#0a0a0a', borderRadius: 8, padding: '8px 10px', textAlign: 'left' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: '#666' }}>Credits</span>
                        <span style={{ fontSize: 12, color: '#ccc', fontWeight: 600 }}>{pack.price}</span>
                      </div>
                      {pack.credits > pack.price && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: '#22c55e' }}>Bonus (+{Math.round(((pack.credits - pack.price) / pack.price) * 100)}%)</span>
                          <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>+{pack.credits - pack.price}</span>
                        </div>
                      )}
                      <div style={{ borderTop: '1px solid #222', paddingTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11, color: GOLD, fontWeight: 700 }}>Total</span>
                        <span style={{ fontSize: 13, color: GOLD, fontWeight: 800 }}>{pack.credits}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Loading skeleton */}
          {loading && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 28 }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={{ background: '#141414', borderRadius: 12, padding: '20px 16px', height: 110 }} className="animate-pulse" />
              ))}
            </div>
          )}

          {/* Custom amount */}
          <div style={{
            background: '#141414', border: '1px solid #222', borderRadius: 12, padding: '20px 20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Zap size={16} style={{ color: GOLD }} />
              <p style={{ fontSize: 14, fontWeight: 700, color: '#ccc', margin: 0 }}>Custom Amount</p>
            </div>
            <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>Enter any amount in ₹ to top up your wallet</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: '#0a0a0a', border: '1px solid #333', borderRadius: 8, padding: '0 12px' }}>
                <span style={{ color: '#666', fontSize: 16, fontWeight: 600 }}>₹</span>
                <input
                  type="number"
                  min="1"
                  value={customAmount}
                  onChange={e => setCustomAmount(e.target.value)}
                  placeholder="Enter amount"
                  style={{
                    flex: 1, background: 'transparent', border: 'none', outline: 'none',
                    color: '#fff', fontSize: 16, fontWeight: 600, padding: '10px 8px',
                  }}
                />
              </div>
              <button
                onClick={handleBuyCustom}
                disabled={!customAmount || parseInt(customAmount) < 1}
                style={{
                  padding: '10px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: GOLD, color: '#111', fontWeight: 700, fontSize: 14,
                  opacity: (!customAmount || parseInt(customAmount) < 1) ? 0.4 : 1,
                }}
              >
                Pay
              </button>
            </div>
          </div>

          {/* Contact */}
          <div style={{
            marginTop: 24, background: 'rgba(255,217,61,0.04)', border: '1px solid rgba(255,217,61,0.15)',
            borderRadius: 12, padding: '16px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
          }}>
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#aaa', margin: '0 0 2px' }}>Need help?</p>
              <p style={{ fontSize: 12, color: '#555', margin: 0 }}>Contact us on WhatsApp for support</p>
            </div>
            <a
              href="https://wa.me/919573766436"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '8px 18px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: '#25D366', color: '#fff', textDecoration: 'none', whiteSpace: 'nowrap',
              }}
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </PageLayout.Content>

      {/* Payment dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#fff' }}>Confirm Payment</DialogTitle>
          </DialogHeader>

          {!purchaseResult && (
            <div style={{ padding: '8px 0' }}>
              <p style={{ fontSize: 14, color: '#aaa', margin: '0 0 12px' }}>{dialogLabel}</p>
              <div style={{ background: '#141414', borderRadius: 10, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: 12, color: '#666', margin: 0 }}>Amount to Pay</p>
                  <p style={{ fontSize: 24, fontWeight: 800, color: '#fff', margin: 0 }}>₹{selectedPack ? selectedPack.price : customPayAmount}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: 12, color: '#666', margin: 0 }}>Credits Added</p>
                  <p style={{ fontSize: 24, fontWeight: 800, color: GOLD, margin: 0 }}>{dialogCredits}</p>
                </div>
              </div>
              <p style={{ fontSize: 11, color: '#555', marginTop: 10 }}>You'll be redirected to a secure payment page (PayU)</p>
            </div>
          )}

          {purchaseResult && (
            <div style={{
              padding: '14px 16px', borderRadius: 8, marginTop: 8,
              background: purchaseResult.success ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${purchaseResult.success ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
              color: purchaseResult.success ? '#22c55e' : '#ef4444', fontSize: 14,
            }}>
              {purchaseResult.message}
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            {!purchaseResult && (
              <>
                <Button variant="outline" onClick={closeDialog} disabled={purchasing}>Cancel</Button>
                <Button onClick={handlePurchase} disabled={purchasing} style={{ background: GOLD, color: '#111' }}>
                  {purchasing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processing...</> : <>Pay Now <ArrowRight className="ml-2 h-4 w-4" /></>}
                </Button>
              </>
            )}
            {purchaseResult && <Button variant="outline" onClick={closeDialog}>Close</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default BuyCreditsPage;
